import React from "react";
import { BrowserRouter, Routes, Route, Navigate } from "react-router-dom";
import { Toaster } from "@/components/ui/sonner";
import { AuthProvider } from "@/context/AuthContext";
import ProtectedRoute from "@/components/ProtectedRoute";
import Layout from "@/components/Layout";
import Login from "@/pages/Login";
import Reminders from "@/pages/Reminders";
import Dashboard from "@/pages/Dashboard";
import AssetsList from "@/pages/AssetsList";
import AssetForm from "@/pages/AssetForm";
import AssetDetail from "@/pages/AssetDetail";
import MasterData from "@/pages/MasterData";
import LeadsList from "@/pages/LeadsList";
import LeadForm from "@/pages/LeadForm";
import LeadDetail from "@/pages/LeadDetail";
import LeadGroups from "@/pages/LeadGroups";
import LeadGroupKanban from "@/pages/LeadGroupKanban";
import LeadSourceMappings from "@/pages/LeadSourceMappings";
import Kanban from "@/pages/Kanban";
import MatchSettings from "@/pages/MatchSettings";
import MatchConfig from "@/pages/MatchConfig";
import TaxonomyCleanup from "@/pages/TaxonomyCleanup";
import SystemSettings from "@/pages/SystemSettings";
import UserManagement from "@/pages/UserManagement";
import AuditLog from "@/pages/AuditLog";
import DealDetail from "@/pages/DealDetail";
import Reports from "@/pages/Reports";
import ReportViewer from "@/pages/ReportViewer";
import BrokersList from "@/pages/BrokersList";
import BrokerDetail from "@/pages/BrokerDetail";
import BulkImport from "@/pages/BulkImport";
import TasksList from "@/pages/TasksList";
import NotificationsPage from "@/pages/NotificationsPage";
import NotesPage from "@/pages/NotesPage";
import MeetingLogger from "@/pages/MeetingLogger";

import "@/App.css";

function Authed({ children }) {
  return (
    <ProtectedRoute><Layout>{children}</Layout></ProtectedRoute>
  );
}

function AdminOnly({ children }) {
  return (
    <ProtectedRoute requireAdmin><Layout>{children}</Layout></ProtectedRoute>
  );
}

function AdminOrRm({ children }) {
  return (
    <ProtectedRoute requireAnyRole={["admin", "relationship_manager"]}>
      <Layout>{children}</Layout>
    </ProtectedRoute>
  );
}

function App() {
  return (
    <AuthProvider>
      <BrowserRouter>
        <Routes>
          <Route path="/login" element={<Login />} />
          <Route path="/" element={<Authed><Dashboard /></Authed>} />
          <Route path="/reminders" element={<Authed><Reminders /></Authed>} />
          <Route path="/notifications" element={<Authed><NotificationsPage /></Authed>} />
          <Route path="/notes" element={<Authed><NotesPage /></Authed>} />

          {/* Assets */}
          <Route path="/assets" element={<Navigate to="/assets/auction" replace />} />
          <Route path="/assets/auction" element={<Authed><AssetsList scope="auction" /></Authed>} />
          <Route path="/assets/land" element={<Authed><AssetsList scope="land" /></Authed>} />
          <Route path="/assets/archived" element={<Authed><AssetsList scope="archived" /></Authed>} />
          <Route path="/assets/new" element={<Authed><AssetForm /></Authed>} />
          <Route path="/assets/:id" element={<Authed><AssetDetail /></Authed>} />
          <Route path="/assets/:id/edit" element={<Authed><AssetForm /></Authed>} />

          {/* Leads */}
          <Route path="/leads" element={<AdminOrRm><LeadsList scope="individual" /></AdminOrRm>} />
          <Route path="/leads/new" element={<AdminOrRm><LeadForm /></AdminOrRm>} />
          <Route path="/leads/:id" element={<AdminOrRm><LeadDetail /></AdminOrRm>} />
          <Route path="/leads/:id/edit" element={<AdminOrRm><LeadForm /></AdminOrRm>} />

          {/* Clients (institutional) — filtered view of the same leads collection */}
          <Route path="/clients" element={<AdminOrRm><LeadsList scope="institutional" /></AdminOrRm>} />
          <Route path="/clients/new" element={<AdminOrRm><LeadForm client /></AdminOrRm>} />
          <Route path="/clients/:id" element={<AdminOrRm><LeadDetail /></AdminOrRm>} />
          <Route path="/clients/:id/edit" element={<AdminOrRm><LeadForm client /></AdminOrRm>} />
          <Route path="/lead-groups" element={<AdminOrRm><LeadGroups scope="individual" /></AdminOrRm>} />
          <Route path="/lead-groups/:groupId/kanban" element={<AdminOrRm><LeadGroupKanban scope="individual" /></AdminOrRm>} />
          <Route path="/client-groups" element={<AdminOrRm><LeadGroups scope="institutional" /></AdminOrRm>} />
          <Route path="/client-groups/:groupId/kanban" element={<AdminOrRm><LeadGroupKanban scope="institutional" /></AdminOrRm>} />

          {/* Matches & Deals (Module 3) */}
          <Route path="/kanban" element={<AdminOrRm><Kanban scope="individual" /></AdminOrRm>} />
          <Route path="/client-pipeline" element={<AdminOrRm><Kanban scope="institutional" /></AdminOrRm>} />
          <Route path="/deals/:id" element={<AdminOrRm><DealDetail /></AdminOrRm>} />

          {/* Reports (Module 6) — per-report RBAC enforced server-side */}
          <Route path="/reports" element={<Authed><Reports /></Authed>} />
          <Route path="/reports/:reportKey" element={<Authed><ReportViewer /></Authed>} />

          {/* Bulk Import (Module 9) — mode-level RBAC enforced server-side */}
          <Route path="/bulk-import" element={<Authed><BulkImport /></Authed>} />

          {/* Broker Network (Module 7) — split into Retail + ICP views (display-only) */}
          <Route path="/brokers" element={<Navigate to="/brokers/retail" replace />} />
          <Route path="/brokers/retail" element={<AdminOrRm><BrokersList scope="retail" /></AdminOrRm>} />
          <Route path="/brokers/icp" element={<AdminOrRm><BrokersList scope="icp" /></AdminOrRm>} />
          <Route path="/brokers/archived" element={<AdminOrRm><BrokersList scope="archived" /></AdminOrRm>} />
          <Route path="/brokers/:id" element={<AdminOrRm><BrokerDetail /></AdminOrRm>} />

          {/* Broker Meeting Log */}
          <Route path="/meetings/new" element={<AdminOrRm><MeetingLogger /></AdminOrRm>} />
          <Route path="/meetings/:id/edit" element={<AdminOrRm><MeetingLogger /></AdminOrRm>} />


          <Route path="/tasks" element={<Authed><TasksList /></Authed>} />
          <Route path="/tasks/:id" element={<Authed><TasksList /></Authed>} />

          {/* Admin-only */}
          <Route path="/master-data" element={<AdminOnly><MasterData /></AdminOnly>} />
          <Route path="/taxonomy-cleanup" element={<AdminOnly><TaxonomyCleanup /></AdminOnly>} />
          <Route path="/lead-source-mappings" element={<AdminOnly><LeadSourceMappings /></AdminOnly>} />
          <Route path="/match-settings" element={<AdminOnly><MatchSettings /></AdminOnly>} />
          <Route path="/match-config" element={<AdminOnly><MatchConfig /></AdminOnly>} />
          <Route path="/system-settings" element={<AdminOnly><SystemSettings /></AdminOnly>} />
          <Route path="/audit-log" element={<AdminOnly><AuditLog /></AdminOnly>} />
          <Route path="/users" element={<AdminOnly><UserManagement /></AdminOnly>} />

          <Route path="*" element={<Navigate to="/" replace />} />
        </Routes>
      </BrowserRouter>
      <Toaster position="top-right" richColors />
    </AuthProvider>
  );
}

export default App;
